import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../Docs.css';

export default function MultiTabPresence() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs/patterns/multi-tab" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Multi-Tab Presence: Avoiding User-Level Flapping</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          Modern web applications routinely run in multiple tabs, windows, and devices at the same
          time.
          <br />
          A correct presence system must treat this as normal behavior, not an edge case.
        </p>

        <p>
          This document explains why <strong>multi-tab presence breaks naïve implementations</strong>{' '}
          and how a <strong>session-first model</strong> avoids user-level flapping and false offline
          states.
        </p>

        <hr />

        <section>
          <h2>The multi-tab problem</h2>
          <p>A single user may:</p>
          <ul>
            <li>open multiple tabs of the same application</li>
            <li>background one tab while another remains active</li>
            <li>close a tab while another is still open</li>
            <li>switch devices during an active session</li>
          </ul>
          <p>If presence is stored at the <strong>user level</strong>, these actions create ambiguity.</p>
          <p>
            A single tab closing should not mark a user offline.
            <br />
            A backgrounded tab should not override an active one.
          </p>
        </section>

        <hr />

        <section>
          <h2>Why user-level presence fails in multi-tab scenarios</h2>
          <p>A common pattern looks like this:</p>
          <ul>
            <li>tab connects → user marked online</li>
            <li>tab disconnects → user marked offline</li>
          </ul>
          <p>With multiple tabs, this produces:</p>
          <ul>
            <li>rapid online/offline flapping</li>
            <li>incorrect availability indicators</li>
            <li>misleading downstream systems</li>
            <li>loss of trust in presence signals</li>
          </ul>
          <p>
            The problem is not WebSockets or Socket.IO.
            <br />
            The problem is <strong>modeling presence at the wrong level</strong>.
          </p>
        </section>

        <hr />

        <section>
          <h2>Session-first presence (the correct model)</h2>
          <p>Presence must be tracked per <strong>session</strong>, not per user.</p>
          <p>A session represents:</p>
          <ul>
            <li>a single browser tab</li>
            <li>a single mobile app instance</li>
            <li>a single SDK runtime</li>
          </ul>
          <p>Each session has its own:</p>
          <ul>
            <li>lifecycle</li>
            <li>heartbeat</li>
            <li>expiration</li>
          </ul>
          <p>Users are <strong>derived aggregates of sessions</strong>, not primary presence entities.</p>
        </section>

        <hr />

        <section>
          <h2>Deriving user presence from sessions</h2>
          <p>User-level presence is computed from active sessions using precedence rules.</p>
          <p>A simple and effective rule set:</p>
          <ul>
            <li>
              <strong>active</strong> beats idle
            </li>
            <li>
              <strong>idle</strong> beats away
            </li>
            <li>
              <strong>away</strong> beats stale
            </li>
            <li>
              <strong>stale</strong> beats offline
            </li>
          </ul>
          <p>This ensures that:</p>
          <ul>
            <li>an active tab keeps the user active</li>
            <li>background tabs do not override foreground activity</li>
            <li>closing one tab does not collapse user presence</li>
          </ul>
          <p>Presence is derived at read time, not written as a mutable user flag.</p>
        </section>

        <hr />

        <section>
          <h2>Handling tab lifecycle events</h2>
          <p>Tabs transition through states independently:</p>
          <ul>
            <li>foreground → background</li>
            <li>background → foreground</li>
            <li>active → idle</li>
            <li>connected → expired</li>
          </ul>
          <p>Presence systems should:</p>
          <ul>
            <li>accept these transitions as signals</li>
            <li>update session state</li>
            <li>recompute user presence without side effects</li>
          </ul>
          <p>
            A tab going idle is <strong>not</strong> a user leaving.
            <br />
            It is one signal among many.
          </p>
        </section>

        <hr />

        <section>
          <h2>Common failure modes</h2>
          <p>Multi-tab issues typically arise from:</p>
          <ul>
            <li>storing <code>user.isOnline</code> as a boolean</li>
            <li>overwriting user presence on any disconnect</li>
            <li>ignoring background visibility state</li>
            <li>expiring presence immediately on socket close</li>
            <li>treating reconnects as new users</li>
          </ul>
          <p>These failures are symptoms of <strong>user-first modeling</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>Multi-tab behavior is normal.</p>
          <p>A correct presence system:</p>
          <ul>
            <li>treats tabs as independent sessions</li>
            <li>derives user presence from session state</li>
            <li>avoids writing user-level online flags</li>
            <li>remains stable under rapid tab changes</li>
          </ul>
          <p>When modeled correctly, multi-tab presence becomes predictable and trustworthy.</p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/patterns/multi-tab.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
